import React, { useCallback, useEffect, useState } from 'react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { Mail, Loader2, AlertCircle, Trash2, Eye, Phone } from 'lucide-react'
import {
  getContactMessages,
  getContactStats,
  updateContactStatus,
  deleteContactMessage,
  STATUS_LABELS,
  PURPOSE_LABELS,
  type ContactMessage,
  type ContactStatus,
} from '@/services/contactService'

const STATUS_FILTERS: (ContactStatus | 'ALL')[] = ['ALL', 'NEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']

const statusTone: Record<string, string> = {
  NEW: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  RESOLVED: 'bg-green-100 text-green-700',
  CLOSED: 'bg-gray-100 text-gray-600',
}

function StatusBadge({ status }: { status: ContactStatus }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {STATUS_LABELS[status]}
    </span>
  )
}

const ContactInbox = React.memo(() => {
  const [messages, setMessages] = useState<ContactMessage[]>([])
  const [stats, setStats] = useState<Record<string, number>>({})
  const [statusFilter, setStatusFilter] = useState<ContactStatus | 'ALL'>('ALL')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [viewing, setViewing] = useState<ContactMessage | null>(null)
  const size = 15

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, s] = await Promise.all([
        getContactMessages({ status: statusFilter, page, size }),
        getContactStats(),
      ])
      setMessages(data.content)
      setTotal(data.totalElements)
      setTotalPages(data.totalPages)
      setStats(s)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load messages.')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    setPage(0)
  }, [statusFilter])

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg })
    setTimeout(() => setToast(null), 3000)
  }

  const changeStatus = async (m: ContactMessage, status: ContactStatus) => {
    setActingId(m.id)
    try {
      await updateContactStatus(m.id, status)
      flash('success', `Marked “${m.name}” as ${STATUS_LABELS[status]}`)
      await load()
    } catch (e: any) {
      flash('error', e?.response?.data?.message || 'Failed to update.')
    } finally {
      setActingId(null)
    }
  }

  const remove = async (m: ContactMessage) => {
    if (!window.confirm(`Delete the message from “${m.name}”? This cannot be undone.`)) return
    setActingId(m.id)
    try {
      await deleteContactMessage(m.id)
      flash('success', 'Message deleted')
      await load()
    } catch (e: any) {
      flash('error', e?.response?.data?.message || 'Failed to delete.')
    } finally {
      setActingId(null)
    }
  }

  const newCount = stats.NEW ?? 0

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle
          title="Contact Inbox"
          subtitle="Triage and respond to messages submitted via the public Contact page"
          icon={Mail}
        />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GovCard className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.TOTAL ?? 0}</p>
            <p className="text-sm text-gray-500">Total</p>
          </GovCard>
          <GovCard className="text-center">
            <p className="text-2xl font-bold text-blue-600">{newCount}</p>
            <p className="text-sm text-gray-500">New</p>
          </GovCard>
          <GovCard className="text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.IN_PROGRESS ?? 0}</p>
            <p className="text-sm text-gray-500">In progress</p>
          </GovCard>
          <GovCard className="text-center">
            <p className="text-2xl font-bold text-green-600">{stats.RESOLVED ?? 0}</p>
            <p className="text-sm text-gray-500">Resolved</p>
          </GovCard>
        </div>

        {/* Status filter tabs */}
        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 mr-1">Status:</span>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f === 'ALL' ? 'All' : STATUS_LABELS[f]}
              </button>
            ))}
            <span className="ml-auto text-xs text-gray-400">{total} total</span>
          </div>
        </GovCard>

        {toast && (
          <div
            role="status"
            className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
              toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            {toast.tone === 'success' ? '✓' : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        {/* Message list */}
        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Messages</h2>
            <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          ) : messages.length === 0 ? (
            <GovEmptyState icon="📭" title="No messages" description="No contact messages match this filter." />
          ) : (
            <div className="divide-y divide-gray-100">
              {messages.map((m) => (
                <div key={m.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <StatusBadge status={m.status} />
                        <span className="text-xs text-gray-400">
                          {m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900">{m.name}</h4>
                      <p className="text-xs text-gray-500">
                        {m.email}
                        {m.phone && (
                          <span className="inline-flex items-center gap-0.5 ml-2">
                            <Phone className="w-3 h-3" /> {m.phone}
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-700 mt-1 line-clamp-2">{m.message}</p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        Purpose: {PURPOSE_LABELS[m.purpose as keyof typeof PURPOSE_LABELS] ?? m.purpose}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {actingId === m.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      <button onClick={() => setViewing(m)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="View">
                        <Eye className="w-4 h-4" />
                      </button>
                      <select
                        value={m.status}
                        onChange={(e) => changeStatus(m, e.target.value as ContactStatus)}
                        disabled={actingId === m.id}
                        className="text-xs border border-gray-200 rounded-md px-2 py-1 disabled:opacity-50"
                        aria-label="Change status"
                      >
                        {(['NEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as ContactStatus[]).map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                      <button onClick={() => remove(m)} disabled={actingId === m.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>

        {/* View modal */}
        {viewing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={() => setViewing(null)}>
            <div className="bg-white rounded-xl max-w-xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <StatusBadge status={viewing.status} />
                  <h3 className="text-xl font-bold text-gray-900 mt-2">{viewing.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {viewing.email}{viewing.phone && ` · ${viewing.phone}`}
                  </p>
                </div>
                <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-700" aria-label="Close">✕</button>
              </div>
              <p className="text-xs text-gray-400 mb-2">
                Purpose: {PURPOSE_LABELS[viewing.purpose as keyof typeof PURPOSE_LABELS] ?? viewing.purpose} · {viewing.createdAt ? new Date(viewing.createdAt).toLocaleString() : ''}
              </p>
              <div className="text-sm text-gray-700 whitespace-pre-wrap border-t border-gray-100 pt-3">{viewing.message}</div>
              {viewing.adminNotes && <p className="mt-4 text-xs text-orange-700 bg-orange-50 p-2 rounded">Admin notes: {viewing.adminNotes}</p>}
            </div>
          </div>
        )}
      </div>
    </GovLayout>
  )
})

ContactInbox.displayName = 'ContactInbox'
export default ContactInbox